import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Flame, Loader2 } from "lucide-react";
import { API } from "../config/api";

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
};

/**
 * Compact "Hot in Congress this week" card for the Market Overview page.
 *
 * Shows the top 5 tickers with the largest BUY-minus-SELL midpoint swing
 * in the last 7 days (from House Clerk PTR PDFs). Politicians often cluster
 * on the same name a few weeks before unusual price moves — surfacing that
 * signal site-wide is high-leverage.
 *
 * Data: GET /api/political-trades/hot-this-week?days=7&limit=5
 * Links each ticker to /stock?ticker=… and the "View all" link to /political-trades.
 */
export default function HotInCongressCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let canc = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/political-trades/hot-this-week?days=7&limit=5`);
        if (!canc) setData(r.data);
      } catch {
        if (!canc) setData(null);
      } finally {
        if (!canc) setLoading(false);
      }
    })();
    return () => { canc = true; };
  }, []);

  return (
    <Card
      className="bg-slate-900/60 border-slate-800 h-full"
      data-testid="hot-in-congress-card"
    >
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-400" /> Hot in Congress · 7d
        </CardTitle>
        <a
          href="/political-trades"
          className="text-[11px] text-emerald-400 hover:text-emerald-300 hover:underline"
          data-testid="hot-in-congress-view-all"
        >
          view all →
        </a>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center text-slate-500 text-xs">
            <Loader2 className="w-3 h-3 animate-spin mr-2" /> Loading…
          </div>
        ) : !data?.rows?.length ? (
          <div className="text-slate-500 text-xs">No qualifying trades in the last 7 days.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left py-1">Ticker</th>
                <th className="text-right py-1">Net swing ≈</th>
                <th className="text-right py-1">B / S</th>
                <th className="text-right py-1 hidden sm:table-cell">Filers</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.ticker} className="border-b border-slate-900 hover:bg-slate-900/40">
                  <td className="py-1.5">
                    <a
                      href={`/stock?ticker=${r.ticker}`}
                      className="font-mono font-bold text-emerald-300 hover:underline"
                      data-testid={`hot-card-ticker-${r.ticker}`}
                    >
                      {r.ticker}
                    </a>
                  </td>
                  <td className={`py-1.5 text-right font-semibold ${
                    r.net_dollar_swing > 0 ? "text-emerald-300" : "text-red-300"
                  }`}>
                    {r.net_dollar_swing > 0 ? "+" : ""}
                    {fmtMoney(r.net_dollar_swing)}
                  </td>
                  <td className="py-1.5 text-right text-slate-400 text-[11px]">
                    <span className="text-emerald-400">{r.buys}</span>
                    {" / "}
                    <span className="text-red-400">{r.sells}</span>
                  </td>
                  <td className="py-1.5 text-right text-slate-400 hidden sm:table-cell">
                    {r.n_filers}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
